import { normalizePath } from './endpoint-identity';

const NON_HTTP_SCHEME_REGEX =
  /^(?:about|blob|brave|chrome|chrome-extension|data|edge|file|ftp|javascript|mailto|moz-extension|opera|safari-extension|tel):/i;

const REGEX_PATTERN_CHARS_REGEX = /[*+?[\]()|\\]/;

const LOCALHOST_ORIGIN_REGEX = /^https?:\/\/localhost(?:[:/?#]|$)/i;

export function expandWwwHostnameVariants(hostname: string): string[] {
  const normalized = hostname.trim().replace(/^\*\./, '').toLowerCase();

  if (!normalized) {
    return [];
  }

  const variants = new Set([normalized]);

  if (normalized.startsWith('www.')) {
    variants.add(normalized.slice(4));
  } else if (normalized !== 'localhost') {
    variants.add(`www.${normalized}`);
  }

  return [...variants];
}

export function areHostsWwwEquivalent(left: string, right: string): boolean {
  const leftVariants = expandWwwHostnameVariants(left);

  return leftVariants.includes(right.trim().replace(/^\*\./, '').toLowerCase());
}

export function isPathOnlyUrl(url: string): boolean {
  const trimmed = url.trim();

  return trimmed.startsWith('/') && !trimmed.startsWith('//');
}

export type ExtractHostnameOptions = {
  assumeHttps?: boolean;
  stripWildcardPrefix?: boolean;
};

export function extractHostname(
  url: string,
  options: ExtractHostnameOptions = {},
): string {
  if (!url) return '';
  if (NON_HTTP_SCHEME_REGEX.test(url)) return '';

  if (isPathOnlyUrl(url)) {
    return '';
  }

  const protocolRelative = toParseableHttpUrl(url);
  const parseable =
    options.assumeHttps && !protocolRelative.includes('://')
      ? `https://${protocolRelative}`
      : protocolRelative;

  if (LOCALHOST_ORIGIN_REGEX.test(parseable)) {
    return 'localhost';
  }

  if (!parseable.includes('://')) {
    if (
      parseable.includes('.') ||
      parseable === 'localhost' ||
      parseable.length > 0
    ) {
      return applyHostnameOptions(parseable.toLowerCase(), options);
    }

    return '';
  }

  try {
    return applyHostnameOptions(
      new URL(parseable).hostname.toLowerCase(),
      options,
    );
  } catch {
    return '';
  }
}

export function isAbsoluteHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function isHostRootUrl(url: string): boolean {
  return isAbsoluteHttpUrl(url) && normalizePath(url) === '';
}

export function normalizeTargetUrl(value: string): string | null {
  try {
    const prefixed = isAbsoluteHttpUrl(value) ? value : `https://${value}`;

    return new URL(prefixed).toString();
  } catch {
    return null;
  }
}

function applyHostnameOptions(
  hostname: string,
  options: ExtractHostnameOptions,
): string {
  return options.stripWildcardPrefix ? hostname.replace(/^\*\./, '') : hostname;
}

export function extractPathname(url: string): string {
  const parseable = toParseableHttpUrl(url);

  if (!parseable.includes('://')) return '/';

  try {
    return new URL(parseable).pathname;
  } catch {
    return '/';
  }
}

export function extractRequestScopePath(url: string): string {
  const parseable = toParseableHttpUrl(url);

  if (!parseable.includes('://')) return '/';

  try {
    const parsed = new URL(parseable);

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '/';
  }
}

function toParseableHttpUrl(url: string): string {
  const trimmed = url.trim();

  return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
}

export function normalizeEnabledDomainEntry(rawDomain: string): string {
  const trimmed = rawDomain.trim();

  if (!trimmed) {
    return '';
  }

  if (isUnsupportedScheme(trimmed)) {
    return '';
  }

  const lower = trimmed.toLowerCase();
  const treatAsUrl =
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('//');

  if (!treatAsUrl) {
    return trimmed;
  }

  try {
    const parseable = lower.startsWith('//') ? `https:${trimmed}` : trimmed;
    const hostname = extractHostname(parseable);
    const pathname = extractPathname(parseable);

    if (!hostname) {
      return '';
    }

    return pathname && pathname !== '/' ? `${hostname}${pathname}` : hostname;
  } catch {
    return '';
  }
}

export function isSubdomain(host: string, parent: string): boolean {
  const normalizedHost = normalizeHostString(host);
  const normalizedParent = normalizeHostString(parent);

  if (!normalizedHost || !normalizedParent) return false;
  if (normalizedHost === normalizedParent) return false;

  return normalizedHost.endsWith(`.${normalizedParent}`);
}

export function isHostExactlyOrSubdomainOf(
  host: string,
  parent: string,
): boolean {
  const normalizedHost = normalizeHostString(host);
  const normalizedParent = normalizeHostString(parent);

  if (!normalizedHost || !normalizedParent) return false;
  if (normalizedHost === normalizedParent) return true;

  if (normalizedParent !== 'localhost' && normalizedHost !== 'localhost') {
    const wwwVariant = normalizedParent.startsWith('www.')
      ? normalizedParent.slice(4)
      : `www.${normalizedParent}`;

    if (normalizedHost === wwwVariant) return true;
  }

  return normalizedHost.endsWith(`.${normalizedParent}`);
}

export function isRegexPattern(domain: string): boolean {
  return REGEX_PATTERN_CHARS_REGEX.test(domain);
}

export function hostHasWildcardOnlyAtLabelStartOrMiddle(host: string): boolean {
  if (!host.includes('*')) {
    return true;
  }

  const labels = host.split('.');
  const lastLabel = labels.at(-1);

  if (!lastLabel) {
    return true;
  }

  return !lastLabel.includes('*');
}

const WILDCARD_PATH_SEGMENT_PATTERN = /^(?:\*|\*\*|\*[^*]+|[^*]+\*)$/;

export function pathHasWildcardOnlyAtSegmentStartOrMiddle(
  path: string,
): boolean {
  if (!path.includes('*')) {
    return true;
  }

  const segments = path.split('/').filter((segment) => segment.length > 0);

  return segments.every(
    (segment) =>
      !segment.includes('*') || WILDCARD_PATH_SEGMENT_PATTERN.test(segment),
  );
}

export function patternMatchesHost(pattern: string, host: string): boolean {
  if (!hostHasWildcardOnlyAtLabelStartOrMiddle(pattern)) {
    return false;
  }

  try {
    const regex = patternToRegex(pattern);

    return regex.test(host.toLowerCase());
  } catch {
    return false;
  }
}

export function patternToRegex(pattern: string): RegExp {
  const regexPattern = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\\\*/g, '.*')
    .replace(/\\\?/g, '.')
    .replace(/\\\[/g, '[')
    .replace(/\\\]/g, ']')
    .replace(/\\\(/g, '(')
    .replace(/\\\)/g, ')')
    .replace(/\\\|/g, '|')
    .replace(/\\\\/g, '\\');

  return new RegExp(`^${regexPattern}$`, 'i');
}

export function pathMatchesPattern(
  requestPath: string,
  configPath: string,
): boolean {
  const normalizedRequestPath = requestPath.startsWith('/')
    ? requestPath
    : `/${requestPath}`;
  const normalizedConfigPath = configPath.startsWith('/')
    ? configPath
    : `/${configPath}`;

  if (normalizedConfigPath.endsWith('/*')) {
    const prefix = normalizedConfigPath.slice(0, -2);

    return (
      normalizedRequestPath.startsWith(prefix + '/') ||
      normalizedRequestPath === prefix ||
      normalizedRequestPath === prefix + '/'
    );
  }

  if (normalizedConfigPath.endsWith('*')) {
    const prefix = normalizedConfigPath.slice(0, -1);

    return normalizedRequestPath.startsWith(prefix);
  }

  return normalizedRequestPath === normalizedConfigPath;
}

export function isUnsupportedScheme(url: string): boolean {
  if (!url) return true;

  return NON_HTTP_SCHEME_REGEX.test(url);
}

function normalizeHostString(value: string): string {
  return value
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .toLowerCase();
}
